import { Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

function CopyBtn({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid="copy-caption-btn"
      onClick={() => {
        navigator.clipboard.writeText(text || "");
        setDone(true);
        toast.success("Copied");
        setTimeout(() => setDone(false), 1500);
      }}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#2A3042] text-[#94A3B8] hover:text-[#F3E5AB] hover:border-[#D4AF37]/40"
    >
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {label}
    </button>
  );
}

function Field({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="mb-4">
      <div className="eyebrow text-[#D4AF37] mb-1">{label}</div>
      <div className={`text-[#F8FAFC] whitespace-pre-wrap leading-relaxed ${mono ? "font-mono2 text-sm" : ""}`}>{value}</div>
    </div>
  );
}

export default function ContentPreview({ content }) {
  if (!content) return null;
  const g = content.generated || {};
  const type = content.content_type;
  const caption = g.caption || g.body || g.personal_text || "";

  return (
    <div className="fade-up" data-testid="content-preview">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display text-2xl text-[#F8FAFC]">{g.title || content.title}</h3>
        <div className="flex gap-2">
          {caption && <CopyBtn text={caption} label="Copy caption" />}
        </div>
      </div>

      {type === "post" && (
        <>
          <Field label="Platform" value={g.platform_recommendation || content.platform} />
          <Field label="Caption" value={g.caption} />
          <Field label="Shorter version" value={g.short_caption} />
          <Field label="Suggested visual" value={g.suggested_visual} />
          <Field label="Best time to post" value={g.suggested_time} />
          {g.hashtags?.length ? <Field label="Hashtags" value={g.hashtags.join("  ")} mono /> : null}
        </>
      )}

      {type === "story" && (
        <>
          <div className="eyebrow text-[#D4AF37] mb-2">Frames</div>
          <div className="space-y-2 mb-4">
            {(g.frames || []).map((f, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#12151E] border border-[#2A3042]">
                <div className="text-xs text-[#64748B] mb-1">Frame {f.frame || i + 1}</div>
                <div className="text-[#F8FAFC]">{f.text}</div>
                {f.media_suggestion && <div className="text-xs text-[#94A3B8] mt-1">Media: {f.media_suggestion}</div>}
              </div>
            ))}
          </div>
          <Field label="CTA frame" value={g.cta_frame} />
          <Field label="Link sticker" value={g.link_sticker} />
          <Field label="Music mood" value={g.music_mood} />
        </>
      )}

      {type === "reel" && (
        <>
          <Field label="Hook" value={g.hook} />
          <Field label="Cover text" value={g.cover_text} />
          <div className="eyebrow text-[#D4AF37] mb-2">Scenes</div>
          <div className="space-y-2 mb-4">
            {(g.scenes || []).map((s, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#12151E] border border-[#2A3042]">
                <div className="text-xs text-[#64748B] mb-1">Scene {s.order || i + 1} · {s.seconds || 3}s</div>
                <div className="text-[#F8FAFC]">{s.text_overlay}</div>
                {s.media_suggestion && <div className="text-xs text-[#94A3B8] mt-1">Media: {s.media_suggestion}</div>}
              </div>
            ))}
          </div>
          <Field label="Caption" value={g.caption} />
          <Field label="Music mood" value={g.music_mood} />
        </>
      )}

      {type === "email" && (
        <>
          <Field label="Subject" value={g.subject} />
          <Field label="Preview text" value={g.preview_text} />
          <Field label="Headline" value={g.headline} />
          <Field label="Body" value={g.body} />
          <Field label="Button text" value={g.button_text} />
          <Field label="Follow-up recommendation" value={g.followup_recommendation} />
        </>
      )}

      {type === "text" && (
        <>
          <Field label="Personal text" value={g.personal_text} />
          <Field label="Family & friends version" value={g.family_friends_version} />
          <Field label="Past guest version" value={g.past_guest_version} />
          <Field label="Concise SMS" value={g.concise_sms} />
          <Field label="Warm lead follow-up" value={g.warm_lead_followup} />
        </>
      )}

      {type === "image" && (
        <>
          <Field label="Overline" value={g.overline} />
          <Field label="Headline" value={g.headline} />
          <Field label="Subhead" value={g.subhead} />
          <Field label="Suggested visual" value={g.suggested_visual} />
          <Field label="Caption" value={g.caption} />
        </>
      )}

      <div className="flex items-center gap-2 mt-2">
        {g.cta && (
          <span className="px-4 py-1.5 rounded-full gold-btn text-sm">{g.cta}</span>
        )}
      </div>
      {g.why && (
        <div className="mt-5 p-4 rounded-lg bg-[#12151E] border border-[#2A3042]">
          <div className="eyebrow text-[#D4AF37] mb-1">Why this?</div>
          <div className="text-sm text-[#94A3B8]">{g.why}</div>
        </div>
      )}
    </div>
  );
}
